/*
 * Copyright 2026 Sony Corporation
 */

import type { DeviceStateResponse, CreativeLook } from '@ips-device-verse/twin-service/schema';

import { CREATIVE_LOOK_FIELDS, NEUTRAL, type CreativeLookField } from './fields';

/**
 * - `settled`: what the device reports, or what nobody has changed.
 * - `pending`: asked for, not yet echoed back by the device.
 * - `rejected`: the write failed and the value shown is the one before the edit.
 */
export type FieldStatus = 'settled' | 'pending' | 'rejected';

export interface LookView {
  values: Required<CreativeLook>;
  status: Record<CreativeLookField, FieldStatus>;
  /** False only where the device says the field is greyed out in its own menu. */
  enabled: Record<CreativeLookField, boolean>;
}

export interface ResolveOptions {
  state: DeviceStateResponse | undefined;
  pending: Partial<CreativeLook>;
  rejected: Partial<Record<CreativeLookField, string>>;
  /** What the device reported for each field when it was edited here. */
  baseline: Partial<CreativeLook>;
  /** Fields whose edit has gone unanswered for long enough to call it refused. */
  timedOut: Partial<Record<CreativeLookField, true>>;
}

/**
 * Decides what each of the nine controls shows, from three sources that are
 * never merged into one mutable blob (devplan 4.2).
 *
 * The precedence is the whole design. `reported` outranks `desired` because it
 * is the device's own answer: a value the body refused comes back different, and
 * the control has to follow it rather than keep showing the request. `desired`
 * still outranks nothing, so a device whose gateway has never run shows what was
 * asked for instead of silently resetting to neutral.
 *
 * An edit is answered when *the reported value moves*, never when the document
 * version does. Writing `desired` bumps that version itself, so the fan-out of
 * the request would otherwise arrive carrying the old reported value and be
 * taken for the reply — which is exactly how a slider snaps back to where it
 * was a moment after being dragged.
 */
export const resolveLook = ({ state, pending, rejected, baseline, timedOut }: ResolveOptions): LookView => {
  const reported = state?.state.reported;
  const desired = state?.state.desired;

  const values = {} as Required<CreativeLook>;
  const status = {} as Record<CreativeLookField, FieldStatus>;
  const enabled = {} as Record<CreativeLookField, boolean>;

  for (const field of CREATIVE_LOOK_FIELDS) {
    const confirmed = reported?.creativeLook?.[field];
    const asked = pending[field];

    // Already what was asked for, or the device has since moved it somewhere
    // else — either way the request has been answered and the device wins.
    const answered = asked === confirmed || confirmed !== baseline[field];
    const outstanding = asked !== undefined && !answered && !timedOut[field];

    values[field] = ((outstanding ? asked : undefined) ??
      confirmed ??
      desired?.creativeLook?.[field] ??
      NEUTRAL[field]) as never;

    const refused = asked !== undefined && asked !== confirmed && (answered || timedOut[field]);

    status[field] = reported && outstanding ? 'pending' : rejected[field] || refused ? 'rejected' : 'settled';

    enabled[field] = reported?.creativeLookEnabled?.[field] !== false;
  }

  return { values, status, enabled };
};
